/**
 * Sonner Toaster wrapped in the brand voice.
 *
 * Design decisions:
 *   - Instrument Serif italic title, Afacad Flux body — matches the
 *     rest of the polished surfaces.
 *   - No `richColors` (sonner's default is too loud). Instead each
 *     toast is a soft white card with a tone-tinted rail on the left
 *     that carries the semantic meaning.
 *   - Icon lives in a tone-tinted rounded tile, not floating.
 *   - Softer shadow, larger radius, and a smaller footprint close pill.
 *   - The `<style>` block scopes overrides to the toaster itself so
 *     nothing else on the page is affected.
 */
import { Toaster } from "sonner";
import {
  AlertCircle, CheckCircle2, Info, Loader2, XCircle,
} from "lucide-react";

const CSS = `
  [data-sonner-toaster] {
    --toast-radius: 16px;
    --toast-shadow: 0 14px 32px rgba(15,82,118,0.12), 0 2px 8px rgba(0,0,0,0.04);
    font-family: 'Afacad Flux', system-ui, sans-serif;
  }
  [data-sonner-toast] {
    background: #fff !important;
    color: #0f172a !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: var(--toast-radius) !important;
    box-shadow: var(--toast-shadow) !important;
    padding: 14px 18px 14px 20px !important;
    min-width: 340px !important;
    max-width: 440px !important;
    gap: 12px !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* Left tone rail — semantic colour per toast type */
  [data-sonner-toast]::before {
    content: '';
    position: absolute;
    left: 0; top: 12%; bottom: 12%;
    width: 4px;
    border-radius: 0 3px 3px 0;
    background: linear-gradient(180deg, #1a5276, #0e9172);
  }
  [data-sonner-toast][data-type='success']::before { background: linear-gradient(180deg, #0e9172, #34d399); }
  [data-sonner-toast][data-type='error']::before   { background: linear-gradient(180deg, #c0392b, #f87171); }
  [data-sonner-toast][data-type='warning']::before { background: linear-gradient(180deg, #b07d2b, #fbbf24); }
  [data-sonner-toast][data-type='info']::before    { background: linear-gradient(180deg, #1a5276, #60a5fa); }
  [data-sonner-toast][data-type='loading']::before { background: linear-gradient(180deg, #64748b, #94a3b8); }

  /* Title — italic serif */
  [data-sonner-toast] [data-title] {
    font-family: 'Instrument Serif', Georgia, serif !important;
    font-style: italic !important;
    font-size: 16.5px !important;
    font-weight: 400 !important;
    color: #0f172a !important;
    line-height: 1.2 !important;
    letter-spacing: 0.005em;
  }

  /* Body */
  [data-sonner-toast] [data-description] {
    font-size: 12.5px !important;
    color: #475569 !important;
    margin-top: 3px !important;
    line-height: 1.55 !important;
  }

  /* Icon tile */
  [data-sonner-toast] [data-icon] {
    width: 36px !important;
    height: 36px !important;
    border-radius: 12px !important;
    padding: 8px !important;
    background: rgba(15,23,42,0.06) !important;
    color: #1a5276 !important;
    display: grid !important;
    place-items: center !important;
    margin-top: 1px !important;
    flex-shrink: 0 !important;
  }
  [data-sonner-toast] [data-icon] > * {
    width: 18px !important;
    height: 18px !important;
  }
  [data-sonner-toast][data-type='success'] [data-icon] { background: rgba(14,145,114,0.14) !important; color: #0e9172 !important; }
  [data-sonner-toast][data-type='error']   [data-icon] { background: rgba(192,57,43,0.14) !important; color: #c0392b !important; }
  [data-sonner-toast][data-type='warning'] [data-icon] { background: rgba(176,125,43,0.14) !important; color: #b07d2b !important; }
  [data-sonner-toast][data-type='info']    [data-icon] { background: rgba(26,82,118,0.14) !important; color: #1a5276 !important; }
  [data-sonner-toast][data-type='loading'] [data-icon] { background: rgba(100,116,139,0.14) !important; color: #64748b !important; }
  [data-sonner-toast][data-type='loading'] [data-icon] > * { animation: pt-spin 0.9s linear infinite; }
  @keyframes pt-spin { to { transform: rotate(360deg); } }

  /* Close button — subtle top-right pill */
  [data-sonner-toast] [data-close-button] {
    top: -8px !important;
    right: -8px !important;
    width: 22px !important;
    height: 22px !important;
    background: #f8fafc !important;
    border: 1px solid #e2e8f0 !important;
    color: #64748b !important;
    box-shadow: 0 2px 6px rgba(15,23,42,0.06) !important;
    transition: transform 0.15s ease, background 0.15s ease !important;
  }
  [data-sonner-toast] [data-close-button]:hover {
    background: #f1f5f9 !important;
    color: #0f172a !important;
    transform: scale(1.05);
  }

  /* Action + cancel buttons — brand pill */
  [data-sonner-toast] [data-button] {
    border-radius: 999px !important;
    padding: 6px 14px !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    letter-spacing: 0.01em;
    box-shadow: 0 6px 14px rgba(26,82,118,0.18) !important;
  }
  [data-sonner-toast] [data-button][data-action] {
    background: #1a5276 !important;
    color: #fff !important;
  }
  [data-sonner-toast] [data-button][data-cancel] {
    background: #f1f5f9 !important;
    color: #475569 !important;
    box-shadow: none !important;
  }

  /* Prefer reduced motion */
  @media (prefers-reduced-motion: reduce) {
    [data-sonner-toast] { transition: none !important; animation: none !important; }
    [data-sonner-toast] [data-close-button] { transition: none !important; }
  }
`;

const PolishedToaster: React.FC<{ position?: React.ComponentProps<typeof Toaster>["position"] }> = ({
  position = "top-right",
}) => (
  <>
    <style>{CSS}</style>
    <Toaster
      position={position}
      expand
      closeButton
      theme="light"
      duration={4500}
      icons={{
        success: <CheckCircle2 />,
        error:   <XCircle />,
        info:    <Info />,
        warning: <AlertCircle />,
        loading: <Loader2 />,
      }}
    />
  </>
);

export default PolishedToaster;
